import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Calendar, MapPin, Clock, Bell, Check, ArrowRight, Play, Pause } from 'lucide-react';
import { getLocalizedField, getImageUrl } from '@/utils/helpers';
import { translateText } from '@/services/translateService';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import TranslationFeedbackButton from '@/components/common/TranslationFeedbackButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// 需要自动翻译的语言
const AUTO_TRANSLATE_LANGS = ['ru', 'uk', 'fr', 'es', 'pt', 'ja', 'ko', 'ar', 'uz'];

// 倒计时计算函数 - 支持开始和结束时间
const getCountdown = (startDate, endDate) => {
  if (!startDate && !endDate) return null;
  
  const now = new Date().getTime();
  const start = startDate ? new Date(startDate).getTime() : null;
  const end = endDate ? new Date(endDate).getTime() : null;
  
  // 活动未开始
  if (start && now < start) {
    const diff = start - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { type: 'starting', days, hours, minutes, seconds, total: diff };
  }
  
  // 活动进行中（有结束时间）
  if (end && now < end) {
    const diff = end - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { type: 'ending', days, hours, minutes, seconds, total: diff };
  }
  
  // 活动已结束
  if (end && now >= end) {
    return { type: 'ended' };
  }
  
  return null;
};

const PosterSection = () => {
  const [items, setItems] = useState([]); // 合并海报和画展
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [countdowns, setCountdowns] = useState({});
  const [reminders, setReminders] = useState({});
  const [reminderLoading, setReminderLoading] = useState({});
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [translatedTexts, setTranslatedTexts] = useState({}); // 自动翻译缓存
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const autoPlayRef = useRef(null);
  
  const currentLang = i18n.language;
  const needsTranslation = AUTO_TRANSLATE_LANGS.includes(currentLang);

  // 自动翻译当前项目内容（仅当数据库中没有手动翻译时）
  useEffect(() => {
    if (!needsTranslation || items.length === 0) return;
    
    const translateCurrentItem = async () => {
      const item = items[activeIndex];
      if (!item) return;
      
      const cacheKey = `${item.id}-${currentLang}`;
      if (translatedTexts[cacheKey]) return;
      
      // 检查数据库中是否已有该语言的翻译
      const dbTitle = item[`title_${currentLang}`];
      const dbSubtitle = item[`subtitle_${currentLang}`] || item[`description_${currentLang}`];
      const dbLocation = item[`event_location_${currentLang}`] || item[`location_${currentLang}`];
      
      // 如果数据库已有翻译，直接使用
      if (dbTitle || dbSubtitle || dbLocation) {
        setTranslatedTexts(prev => ({
          ...prev,
          [cacheKey]: {
            title: dbTitle || '',
            subtitle: dbSubtitle || '',
            location: dbLocation || ''
          }
        }));
        return;
      }
      
      // 否则调用翻译API
      try {
        const sourceTitle = item.title_en || item.title || '';
        const sourceSubtitle = item.subtitle_en || item.subtitle || item.description_en || item.description || '';
        const sourceLocation = item.event_location_en || item.event_location || item.location_en || item.location || '';
        
        const [translatedTitle, translatedSubtitle, translatedLocation] = await Promise.all([
          sourceTitle ? translateText(sourceTitle, currentLang) : '',
          sourceSubtitle ? translateText(sourceSubtitle, currentLang) : '',
          sourceLocation ? translateText(sourceLocation, currentLang) : ''
        ]);
        
        setTranslatedTexts(prev => ({
          ...prev,
          [cacheKey]: {
            title: translatedTitle,
            subtitle: translatedSubtitle,
            location: translatedLocation
          }
        }));
      } catch (err) {
        console.error('Translation error:', err);
      }
    };
    
    translateCurrentItem();
  }, [items, activeIndex, currentLang, needsTranslation, translatedTexts]);

  // 获取翻译后的文本（优先使用数据库翻译）
  const getTranslatedText = (item, field) => {
    if (!needsTranslation) {
      return getLocalizedField(item, field, currentLang);
    }
    
    // 优先检查数据库中的翻译
    const dbField = `${field}_${currentLang}`;
    if (item[dbField] && item[dbField].trim()) {
      return item[dbField];
    }
    
    // 然后检查翻译缓存
    const cacheKey = `${item.id}-${currentLang}`;
    const cached = translatedTexts[cacheKey];
    
    if (cached && cached[field]) {
      return cached[field];
    }
    
    // 回退到原始文本
    return getLocalizedField(item, field, currentLang);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [postersRes, exhibitionsRes] = await Promise.all([
        axios.get(`${API}/posters?limit=10`),
        axios.get(`${API}/exhibitions?limit=10`)
      ]);
      
      // 将海报转换为统一格式，使用新的 start_time 和 end_time 字段
      const posterItems = postersRes.data.map(poster => ({
        ...poster,
        itemType: 'poster',
        start_date: poster.start_time || poster.event_date, // 兼容旧数据
        end_date: poster.end_time,
      }));
      
      // 筛选出show_in_events=true的画展并转换格式
      const exhibitionItems = (exhibitionsRes.data || [])
        .filter(ex => ex.show_in_events && (ex.status === 'published' || ex.status === 'approved'))
        .map(exhibition => ({
          ...exhibition,
          itemType: 'exhibition',
          image_url: exhibition.cover_image,
          subtitle: exhibition.description,
          event_location: exhibition.location,
        }));
      
      // 合并并按创建时间排序
      const allItems = [...posterItems, ...exhibitionItems].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setItems(allItems);
      
      // 获取海报提醒状态
      if (token && posterItems.length > 0) {
        const reminderStatuses = {};
        await Promise.all(
          posterItems.map(async (poster) => {
            try {
              const statusRes = await axios.get(`${API}/posters/${poster.id}/remind/status`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              reminderStatuses[poster.id] = statusRes.data.has_reminder;
            } catch (err) {
              reminderStatuses[poster.id] = false;
            }
          })
        );
        setReminders(reminderStatuses);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  // 倒计时更新
  useEffect(() => {
    if (items.length === 0) return;
    
    const updateCountdowns = () => {
      const newCountdowns = {};
      items.forEach(item => {
        const countdown = getCountdown(item.start_date, item.end_date);
        if (countdown) {
          newCountdowns[item.id] = countdown;
        }
      });
      setCountdowns(newCountdowns);
    };
    
    updateCountdowns();
    const timer = setInterval(updateCountdowns, 1000);
    
    return () => clearInterval(timer);
  }, [items]);

  // 自动轮播
  useEffect(() => {
    if (items.length > 1 && isAutoPlaying) {
      autoPlayRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % items.length);
      }, 6000);
    }
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [items.length, isAutoPlaying]);

  // 设置/取消提醒
  const handleReminder = async (e, itemId) => {
    e.stopPropagation();
    
    if (!user || !token) {
      toast.error(t('poster.loginToRemind', '请先登录以设置提醒'));
      navigate('/login');
      return;
    }
    
    setReminderLoading(prev => ({ ...prev, [itemId]: true }));
    
    try {
      if (reminders[itemId]) {
        await axios.delete(`${API}/posters/${itemId}/remind`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReminders(prev => ({ ...prev, [itemId]: false }));
        toast.success(t('poster.reminderCancelled', '提醒已取消'));
      } else {
        await axios.post(`${API}/posters/${itemId}/remind`, null, {
          headers: { Authorization: `Bearer ${token}` },
          params: { remind_before_hours: 24 }
        });
        setReminders(prev => ({ ...prev, [itemId]: true }));
        toast.success(t('poster.reminderSet', '提醒设置成功，活动开始前24小时通知您'));
      }
    } catch (err) {
      const message = err.response?.data?.detail || t('poster.reminderError', '操作失败');
      toast.error(message);
    } finally {
      setReminderLoading(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleItemClick = async (item) => {
    if (item.itemType === 'exhibition') {
      navigate(`/exhibitions/${item.id}`);
    } else {
      // 记录点击
      try {
        await axios.post(`${API}/posters/${item.id}/click`);
      } catch (err) {}
      
      // 跳转
      if (item.link_url) {
        if (item.link_url.startsWith('http')) {
          // 外部链接：在当前窗口跳转
          window.location.href = item.link_url;
        } else {
          navigate(item.link_url);
        }
      }
      // 如果没有链接，不执行任何操作（用户可以查看海报信息）
    }
  };

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % items.length);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  const getEventTypeLabel = (item) => {
    if (item.itemType === 'exhibition') {
      return t('poster.exhibition', '画展');
    }
    const labels = {
      exhibition: t('poster.exhibition', '艺术展'),
      film: t('poster.film', '电影'),
      theater: t('poster.theater', '戏剧'),
      other: t('poster.other', '活动'),
    };
    return labels[item.event_type] || labels.other;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const isZh = i18n.language.startsWith('zh');
    return date.toLocaleDateString(isZh ? 'zh-CN' : (i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US'), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // 渲染倒计时组件
  const renderCountdown = (countdown, itemId) => {
    if (!countdown || countdown.type === 'ended') return null;
    
    const isStarting = countdown.type === 'starting';
    const label = isStarting 
      ? t('poster.comingSoon', '即将开始') 
      : t('poster.endingSoon', '即将结束');
    const bgColor = isStarting ? 'bg-amber-500/90' : 'bg-red-500/90';
    
    return (
      <div 
        className={`${bgColor} backdrop-blur-md rounded-xl px-4 py-3 text-white shadow-lg`}
        data-testid={`countdown-${itemId}`}
      >
        <div className="flex items-center gap-1 mb-2">
          <Clock className="w-4 h-4" />
          <span className="text-sm font-medium">{label}</span>
        </div>
        <div className="flex items-center gap-2 text-center">
          <div className="bg-white/20 rounded-lg px-2 py-1 min-w-[45px]">
            <span className="text-xl font-bold tabular-nums">{countdown.days}</span>
            <span className="text-[10px] block opacity-80">{t('poster.days', '天')}</span>
          </div>
          <span className="text-lg opacity-60">:</span>
          <div className="bg-white/20 rounded-lg px-2 py-1 min-w-[45px]">
            <span className="text-xl font-bold tabular-nums">{String(countdown.hours).padStart(2, '0')}</span>
            <span className="text-[10px] block opacity-80">{t('poster.hours', '时')}</span>
          </div>
          <span className="text-lg opacity-60">:</span>
          <div className="bg-white/20 rounded-lg px-2 py-1 min-w-[45px]">
            <span className="text-xl font-bold tabular-nums">{String(countdown.minutes).padStart(2, '0')}</span>
            <span className="text-[10px] block opacity-80">{t('poster.minutes', '分')}</span>
          </div>
          <span className="text-lg opacity-60">:</span>
          <div className="bg-white/20 rounded-lg px-2 py-1 min-w-[45px]">
            <span className="text-xl font-bold tabular-nums">{String(countdown.seconds).padStart(2, '0')}</span>
            <span className="text-[10px] block opacity-80">{t('poster.seconds', '秒')}</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <section className="py-16 px-6 bg-amber-50/50">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-amber-100 rounded mb-8"></div>
            <div className="h-80 bg-amber-100 rounded-3xl"></div>
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return null;
  }

  const currentItem = items[activeIndex];

  return (
    <section className="py-16 px-6 bg-amber-50/50" data-testid="poster-section">
      <div className="max-w-6xl mx-auto">
        {/* 标题和控制栏 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-light text-amber-900 mb-1">
              {t('poster.sectionTitle', '精彩活动')}
            </h2>
            <p className="text-amber-700/70 text-sm">
              {t('poster.sectionSubtitle', '探索最新的艺术活动与画展')}
            </p>
          </div>
          
          {items.length > 1 && (
            <div className="flex items-center gap-2">
              {/* 自动播放控制 */}
              <button
                onClick={toggleAutoPlay}
                className="w-10 h-10 rounded-full border border-amber-300 flex items-center justify-center text-amber-700 hover:bg-amber-100 transition-colors"
                title={isAutoPlaying ? t('poster.pause', '暂停') : t('poster.play', '播放')}
              >
                {isAutoPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <button
                onClick={handlePrev}
                className="w-10 h-10 rounded-full border border-amber-300 flex items-center justify-center text-amber-700 hover:bg-amber-100 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={handleNext}
                className="w-10 h-10 rounded-full border border-amber-300 flex items-center justify-center text-amber-700 hover:bg-amber-100 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* 左图右文字布局 */}
        <div 
          className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer"
          onClick={() => handleItemClick(currentItem)}
          data-testid={`event-item-${activeIndex}`}
        >
          <div className="flex flex-col lg:flex-row">
            {/* 左侧图片 */}
            <div className="lg:w-1/2 relative aspect-[16/10] lg:aspect-auto lg:min-h-[400px] overflow-hidden">
              <img
                src={getImageUrl(currentItem.image_url)}
                alt={getTranslatedText(currentItem, 'title')}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
              {/* 类型标签 */}
              <div className="absolute top-4 left-4">
                <span className="inline-block px-4 py-1.5 bg-amber-500 text-white rounded-full text-sm font-medium shadow-lg">
                  {getEventTypeLabel(currentItem)}
                </span>
              </div>
              {/* 进度指示器 */}
              {items.length > 1 && (
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex gap-1.5">
                    {items.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveIndex(index);
                        }}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          index === activeIndex
                            ? 'flex-1 bg-white'
                            : 'w-6 bg-white/50 hover:bg-white/70'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* 右侧文字区 */}
            <div className="lg:w-1/2 p-6 md:p-8 lg:p-10 flex flex-col justify-between">
              <div>
                {/* 标题 */}
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-light text-amber-900 mb-4 leading-tight flex items-start gap-2">
                  <span>{getTranslatedText(currentItem, 'title')}</span>
                  {needsTranslation && (
                    <TranslationFeedbackButton
                      contentType={currentItem.itemType || 'poster'}
                      contentId={currentItem.id}
                      fieldName="title"
                      language={currentLang}
                      originalText={currentItem.title_en || currentItem.title}
                      translatedText={getTranslatedText(currentItem, 'title')}
                      size="md"
                    />
                  )}
                </h3>
                
                {/* 描述 */}
                {(currentItem.subtitle || currentItem.description) && (
                  <div className="text-amber-700/80 text-base md:text-lg mb-6 line-clamp-3 flex items-start gap-2">
                    <span>
                      {getTranslatedText(currentItem, 'subtitle') || 
                       getTranslatedText(currentItem, 'description')}
                    </span>
                    {needsTranslation && (
                      <TranslationFeedbackButton
                        contentType={currentItem.itemType || 'poster'}
                        contentId={currentItem.id}
                        fieldName="subtitle"
                        language={currentLang}
                        originalText={currentItem.subtitle_en || currentItem.subtitle || currentItem.description_en || currentItem.description}
                        translatedText={getTranslatedText(currentItem, 'subtitle') || getTranslatedText(currentItem, 'description')}
                        size="sm"
                      />
                    )}
                  </div>
                )}
                
                {/* 信息标签 */}
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  {currentItem.start_date && (
                    <span className="flex items-center gap-1.5 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full text-sm">
                      <Calendar className="w-4 h-4" />
                      {formatDate(currentItem.start_date)}
                      {currentItem.end_date && ` - ${formatDate(currentItem.end_date)}`}
                    </span>
                  )}
                  {currentItem.event_location && (
                    <span className="flex items-center gap-1.5 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full text-sm">
                      <MapPin className="w-4 h-4" />
                      {getTranslatedText(currentItem, 'location') || getTranslatedText(currentItem, 'event_location')}
                    </span>
                  )}
                </div>
                
                {/* 倒计时 */}
                {countdowns[currentItem.id] && (
                  <div className="mb-6">
                    {renderCountdown(countdowns[currentItem.id], currentItem.id)}
                  </div>
                )}
              </div>
              
              {/* 底部操作区 */}
              <div className="flex items-center justify-between pt-4 border-t border-amber-100">
                {/* 提醒按钮 - 仅海报且活动未开始时显示 */}
                {currentItem.itemType === 'poster' && countdowns[currentItem.id]?.type === 'starting' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleReminder(e, currentItem.id)}
                    disabled={reminderLoading[currentItem.id]}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      reminders[currentItem.id]
                        ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
                        : 'border-amber-300 text-amber-700 hover:bg-amber-50'
                    }`}
                    data-testid={`remind-btn-${currentItem.id}`}
                  >
                    {reminderLoading[currentItem.id] ? (
                      <span className="animate-pulse">...</span>
                    ) : reminders[currentItem.id] ? (
                      <>
                        <Check className="w-4 h-4 mr-1.5" />
                        {t('poster.reminded', '已设提醒')}
                      </>
                    ) : (
                      <>
                        <Bell className="w-4 h-4 mr-1.5" />
                        {t('poster.remindMe', '提醒我')}
                      </>
                    )}
                  </Button>
                )}
                
                <div className="flex-1" />
                
                {/* 进入按钮 */}
                <span className="inline-flex items-center text-amber-800 font-medium hover:text-amber-600 transition-colors">
                  {currentItem.itemType === 'exhibition' 
                    ? t('poster.enterExhibition', '进入画展') 
                    : t('poster.viewDetails', '查看详情')}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 底部快速导航 */}
        {items.length > 3 && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {items.slice(0, 4).map((item, index) => (
              <button
                key={item.id}
                onClick={() => setActiveIndex(index)}
                className={`p-3 rounded-xl text-left transition-all ${
                  index === activeIndex 
                    ? 'bg-amber-100 border-2 border-amber-300' 
                    : 'bg-white border border-amber-100 hover:border-amber-200'
                }`}
              >
                <span className={`text-xs font-medium ${index === activeIndex ? 'text-amber-700' : 'text-amber-500'}`}>
                  {getEventTypeLabel(item)}
                </span>
                <p className={`text-sm mt-1 line-clamp-1 ${index === activeIndex ? 'text-amber-900' : 'text-amber-700'}`}>
                  {getLocalizedField(item, 'title', i18n.language)}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PosterSection;
